import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSubscriptionById } from '../hooks/useSubscriptionById'
import { useRequireAuth } from '../hooks/useRequireAuth'
import { useCheckoutBundleStore } from '../store/checkoutBundleStore'
import { trackViewContent, trackAddToCart } from '../utils/metaPixel'
import SubscriptionDetailTemplate from '../components/SubscriptionDetailTemplate'

/**
 * Book of the Month detail page.
 * Loads the subscription from the backend API.
 */
export default function BookOfTheMonthPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const requireAuth = useRequireAuth()
  const { subscription: sub, loading, error } = useSubscriptionById('book-of-the-month')
  const { addTier, items, isMaxReached } = useCheckoutBundleStore()

  const bundleItem = items.find((item) => item.slug === sub?.id)
  const inBundle = Boolean(bundleItem)
  const bundleQuantity = bundleItem?.quantity || 0
  const maxReached = isMaxReached()

  useEffect(() => {
    if (sub) {
      trackViewContent(sub)
    }
  }, [sub])

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 pb-12 text-center">
        <p className="text-brand-blue">{t('common.loading')}</p>
      </div>
    )
  }

  if (error || !sub) {
    return (
      <div className="max-w-5xl mx-auto px-4 pb-12 text-center">
        <p className="text-red-600">{error || t('common.loading')}</p>
      </div>
    )
  }

  const handleChoose = () => {
    const returnUrl = `${window.location.origin}/checkout`
    const authUrl = requireAuth(returnUrl)
    if (authUrl !== returnUrl) {
      // User is not authenticated; requireAuth will redirect to backend login.
      window.location.href = authUrl
      return
    }
    addTier(sub)
    trackAddToCart(sub)
    navigate('/checkout')
  }

  const handleAddToBundle = () => {
    addTier(sub)
    trackAddToCart(sub)
  }

  const handleGoToCheckout = () => {
    navigate('/checkout')
  }

  return (
    <div className="max-w-5xl mx-auto px-4 pb-12 min-h-[calc(100vh-10rem)] flex flex-col">
      <div className="text-center mb-8">
        <h1 className="font-climate text-4xl md:text-6xl text-brand-blue mb-3">
          {t('nav.individualSubscription')}
        </h1>
      </div>

      <div className="flex-1 flex items-start justify-center pt-4 md:pt-[7vh]">
        <SubscriptionDetailTemplate
          subscription={sub}
          mode="recurring"
          inBundle={inBundle}
          bundleQuantity={bundleQuantity}
          maxReached={maxReached}
          onChoose={handleChoose}
          onAddToBundle={handleAddToBundle}
          onGoToCheckout={handleGoToCheckout}
        />
      </div>
    </div>
  )
}
